import { useEffect, useRef, useCallback } from "react";
import quickwordProcessor from "../common/quickword-processor";
import { useRecoilValue } from "recoil";
import { scopePreferenceState } from "../global-state";

const useNativeInputBridge = ({
  prompts = [],
  onSendMessage,
  onContentSync,
  placeholder = "",
  // Data fetching functions
  dataFetchers = {},
  appInterface = null,
}) => {
  const isApp = useRef(appInterface?.isAvailable() || false);
  const bridgeRef = useRef(null);
  const currentContentRef = useRef("");
  const scopePreference = useRecoilValue(scopePreferenceState);

  // Initialize bridge
  useEffect(() => {
    if (!isApp.current) return;

    // Initialize bridge class
    class AssistantInputBridge {
      constructor(dataFetchers) {
        this.dataFetchers = dataFetchers;
        this.setupGlobalCallbacks();
      }

      setupGlobalCallbacks() {
        // Handle requests for entity types (@ mentions)
        window.onAssistantRequestEntityTypes = async () => {
          console.log("Android requesting entity types");
          if (dataFetchers.fetchEntityTypes) {
            try {
              const entityTypes = await dataFetchers.fetchEntityTypes();
              this.sendEntityTypes(entityTypes);
            } catch (error) {
              console.error("Error handling entity types request:", error);
            }
          }
        };

        // Handle requests for entities by type
        window.onAssistantRequestEntities = async (entityType) => {
          console.log("Android requesting entities for type:", entityType);
          if (dataFetchers.fetchEntities) {
            try {
              const entities = await dataFetchers.fetchEntities(entityType);
              this.sendEntities(entityType, entities);
            } catch (error) {
              console.error("Error handling entities request:", error);
            }
          }
        };

        // Handle requests for quick words (# mentions)
        window.onAssistantRequestQuickWords = async () => {
          console.log("Android requesting quick words");
          if (dataFetchers.fetchQuickWords) {
            try {
              const quickWords = await dataFetchers.fetchQuickWords();
              this.sendQuickWords(quickWords);
            } catch (error) {
              console.error("Error handling quick words request:", error);
            }
          }
        };

        // Handle send message from Android
        window.onAssistantMessageSent = (message, mentionsJson) => {
          console.log("Android message sent:", message, mentionsJson);
          if (onSendMessage) {
            try {
              const mentions = mentionsJson ? JSON.parse(mentionsJson) : {};
              onSendMessage(message, mentions);
            } catch (e) {
              console.error("Error parsing mentions:", e);
              onSendMessage(message, {});
            }
          }
        };

        // Handle content sync when Android input changes/blurs
        window.onAssistantInputChanged = (text, promptJson) => {
          console.log("Android input changed:", text, promptJson);
          currentContentRef.current = text;
          if (onContentSync) {
            try {
              const prompt = promptJson ? JSON.parse(promptJson) : null;
              onContentSync(text, prompt);
            } catch (e) {
              console.error(
                "Error parsing mentions/prompt in onContentSync:",
                e
              );
              onContentSync(text, null);
            }
          }
        };

        // Handle quickword processing requests from native
        window.onAssistantProcessQuickword = async (quickwordText, scopeId) => {
          console.log(
            "Android requesting quickword processing:",
            quickwordText
          );
          try {
            // Check if we can process synchronously
            if (quickwordProcessor.canProcessSync(quickwordText)) {
              const processedText =
                quickwordProcessor.processQuickwordSync(quickwordText);
              this.sendProcessedQuickword(processedText, null);
            } else {
              // Async processing needed
              const processedText = await quickwordProcessor.processQuickword(
                quickwordText,
                scopePreference[scopeId]
              );
              this.sendProcessedQuickword(processedText, null);
            }
          } catch (error) {
            console.error("Error processing quickword:", error);
            this.sendProcessedQuickword(quickwordText, error.message); // Return original text on error
          }
        };

        // Handle focus changes
        window.onAssistantInputFocus = (hasFocus) => {
          console.log("Android input focus changed:", hasFocus);
        };
      }

      // Core bridge methods
      async show() {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.showAssistantInput();
          } catch (error) {
            console.error("Error showing assistant input:", error);
          }
        }
      }

      async hide() {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.hideAssistantInput();
          } catch (error) {
            console.error("Error hiding assistant input:", error);
          }
        }
      }

      async setText(text, prompt = {}) {
        if (appInterface?.isAvailable()) {
          const promptJson = JSON.stringify(prompt || {});
          await appInterface.setAssistantInputText(text || "", promptJson);
          currentContentRef.current = text || "";
        }
      }

      async getText() {
        if (appInterface?.isAvailable()) {
          try {
            return await appInterface.getAssistantInputText();
          } catch (error) {
            console.error("Error getting assistant input text:", error);
          }
        }
        return currentContentRef.current;
      }

      async setPlaceholder(placeholder) {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.setAssistantInputPlaceholder(placeholder || "");
          } catch (error) {
            console.error("Error setting assistant input placeholder:", error);
          }
        }
      }

      async setPrompts(prompts) {
        if (appInterface?.isAvailable() && Array.isArray(prompts)) {
          try {
            const promptsJson = JSON.stringify(prompts);
            await appInterface.setAssistantPrompts(promptsJson);
          } catch (e) {
            console.error("Error serializing prompts:", e);
          }
        }
      }

      async focus() {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.focusAssistantInput();
          } catch (error) {
            console.error("Error focusing assistant input:", error);
          }
        }
      }

      async blur() {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.blurAssistantInput();
          } catch (error) {
            console.error("Error blurring assistant input:", error);
          }
        }
      }

      // New methods to send data back to native interface
      async sendEntityTypes(entityTypes) {
        if (appInterface?.isAvailable() && Array.isArray(entityTypes)) {
          try {
            await appInterface.setAssistantEntityTypes(
              JSON.stringify(entityTypes)
            );
          } catch (e) {
            console.error("Error sending entity types to native interface:", e);
          }
        }
      }

      async sendEntities(entityType, entities) {
        if (appInterface?.isAvailable() && Array.isArray(entities)) {
          try {
            await appInterface.setAssistantEntities(
              entityType,
              JSON.stringify(entities)
            );
          } catch (e) {
            console.error("Error sending entities to native interface:", e);
          }
        }
      }

      async sendQuickWords(quickWords) {
        if (appInterface?.isAvailable() && Array.isArray(quickWords)) {
          try {
            await appInterface.setAssistantQuickWords(
              JSON.stringify(quickWords)
            );
          } catch (e) {
            console.error("Error sending quick words to native interface:", e);
          }
        }
      }

      async sendProcessedQuickword(processedText, error) {
        if (appInterface?.isAvailable()) {
          try {
            await appInterface.setAssistantProcessedQuickword(
              processedText || "",
              error || null
            );
          } catch (e) {
            console.error(
              "Error sending processed quickword to native interface:",
              e
            );
          }
        }
      }
    }

    bridgeRef.current = new AssistantInputBridge(dataFetchers);

    return () => {
      // Cleanup global callbacks
      if (typeof window !== "undefined") {
        window.onAssistantRequestEntityTypes = null;
        window.onAssistantRequestEntities = null;
        window.onAssistantRequestQuickWords = null;
        window.onAssistantMessageSent = null;
        window.onAssistantInputChanged = null;
        window.onAssistantInputFocus = null;
        window.onAssistantProcessQuickword = null;
      }
    };
  }, [onSendMessage, onContentSync, dataFetchers]);

  // Update prompts when they change
  useEffect(() => {
    if (isApp.current && bridgeRef.current && prompts.length > 0) {
      bridgeRef.current.setPrompts(prompts);
    }
  }, [prompts]);

  // Update placeholder when it changes
  useEffect(() => {
    if (isApp.current && bridgeRef.current && placeholder) {
      bridgeRef.current.setPlaceholder(placeholder);
    }
  }, [placeholder]);

  // Bridge interface methods
  const showNativeInput = useCallback((content = "", prompt = {}) => {
    if (!isApp.current || !bridgeRef.current) return false;

    try {
      // Sync content, mentions, and prompt to Android before showing
      bridgeRef.current.setText(content, prompt);
      bridgeRef.current.show();
      bridgeRef.current.focus();
      return true;
    } catch (error) {
      console.error("Error showing native input:", error);
      return false;
    }
  }, []);

  const hideNativeInput = useCallback(() => {
    if (!isApp.current || !bridgeRef.current) return false;

    try {
      bridgeRef.current.hide();
      return true;
    } catch (error) {
      console.error("Error hiding native input:", error);
      return false;
    }
  }, []);

  const syncContentToNative = useCallback(
    (content, mentions = {}, prompt = {}) => {
      if (!isApp.current || !bridgeRef.current) return false;

      try {
        bridgeRef.current.setText(content, prompt);
        return true;
      } catch (error) {
        console.error("Error syncing content to native:", error);
        return false;
      }
    },
    []
  );

  const getCurrentContent = useCallback(() => {
    if (!isApp.current || !bridgeRef.current) return "";

    try {
      return bridgeRef.current.getText();
    } catch (error) {
      console.error("Error getting current content:", error);
      return "";
    }
  }, []);

  return {
    isNativeSupported: isApp.current,
    showNativeInput,
    hideNativeInput,
    syncContentToNative,
    getCurrentContent,
  };
};

export default useNativeInputBridge;
